// What the chat composer accepts is decided by the backend and fetched
// (`useDocumentFormats`, AB#5986). This module is the pure part: given that
// list, which file passes.
//
// It used to hard-code its own allowlist and caps — a second, tighter copy of
// the Knowledge Base's list. That is why the two paths drifted: a format added
// to the KB was simply absent here, and a real 6.8 MB plant export was refused
// by a cap that existed to protect a synchronous parse the backend no longer
// does in the request (AB#5990).
//
// Images are matched on their MIME type, everything else on its extension —
// the backend does the same, because browsers label Office files
// inconsistently (Windows Chrome even reports .csv as
// application/vnd.ms-excel).

import type {DocumentFormat} from '@interfaces/alpinist/document'

/** Which types the model takes as pixels rather than as extracted text. */
export const INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

export const isImageMimeType = (mimeType: string): boolean => INLINE_IMAGE_TYPES.includes(mimeType)

const IMAGE_FILENAME_RE = /\.(png|jpe?g|webp|gif)$/i

/**
 * An attachment to draw as an image. By its type, or by its name when the
 * type is missing: a look_at_screen capture (AB#6762) comes back without one.
 */
export const isImageAttachment = (attachment: {
  mime_type?: string | null
  original_filename?: string | null
}): boolean =>
  isImageMimeType(attachment.mime_type ?? '') ||
  (!attachment.mime_type && IMAGE_FILENAME_RE.test(attachment.original_filename ?? ''))

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot).toLowerCase() : ''
}

export const formatFor = (
  formats: readonly DocumentFormat[],
  file: File,
): DocumentFormat | undefined => {
  const extension = extensionOf(file.name)
  const byExtension = formats.find((format) => format.extension === extension)
  if (byExtension) return byExtension
  // A file dropped without a usable name still has a type the browser knows.
  return formats.find((format) => format.mime_type === file.type)
}

export const isImageFile = (file: File): boolean => isImageMimeType(file.type)

export const isAcceptedFile = (file: File, formats: readonly DocumentFormat[]): boolean => {
  const format = formatFor(formats, file)
  return Boolean(format) && file.size <= (format as DocumentFormat).max_chat_bytes
}

/** Why a file was refused, for the message shown next to it. */
export type ChatRejection = 'type' | 'size'

export const chatRejection = (
  file: File,
  formats: readonly DocumentFormat[],
): ChatRejection | null => {
  const format = formatFor(formats, file)
  if (!format) return 'type'
  if (file.size > format.max_chat_bytes) return 'size'
  return null
}

export const maxChatBytesFor = (file: File, formats: readonly DocumentFormat[]): number =>
  formatFor(formats, file)?.max_chat_bytes ?? 0

export type BatchRejection = 'limit' | 'size' | 'type'

/**
 * Why a picked or dropped batch was refused, in whole or in part — null when it
 * all fits. Dropping a file and having nothing happen is the worst outcome of a
 * drag-and-drop, so the composer says which rule got in the way.
 */
export const rejectionReason = (
  files: File[],
  room: number,
  formats: readonly DocumentFormat[],
): BatchRejection | null => {
  const accepted = files.filter((file) => isAcceptedFile(file, formats))
  if (accepted.length > Math.max(0, room)) return 'limit'

  const refused = files.filter((file) => !isAcceptedFile(file, formats))
  if (!refused.length) return null

  // A file of a supported type can only have been refused on size.
  return refused.some((file) => Boolean(formatFor(formats, file))) ? 'size' : 'type'
}

/** Which icon a chip shows — the file's kind, from the MIME type. */
export type AttachmentKind = 'image' | 'spreadsheet' | 'pdf' | 'word' | 'presentation' | 'file'

const KIND_BY_MIME_TYPE: Record<string, AttachmentKind> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12': 'spreadsheet',
  'text/csv': 'spreadsheet',
}

const KIND_BY_EXTENSION: Record<string, AttachmentKind> = {
  '.pdf': 'pdf',
  '.docx': 'word',
  '.pptx': 'presentation',
  '.xlsx': 'spreadsheet',
  '.xlsm': 'spreadsheet',
  '.csv': 'spreadsheet',
}

export const attachmentKind = (mimeType: string): AttachmentKind => {
  if (isImageMimeType(mimeType)) return 'image'
  return KIND_BY_MIME_TYPE[mimeType] || 'file'
}

/**
 * The kind of a file the user has only just picked. Resolved from the extension
 * because browsers send an empty type for .docx and .pptx.
 */
export const pendingAttachmentKind = (file: File): AttachmentKind => {
  if (isImageFile(file)) return 'image'
  return KIND_BY_EXTENSION[extensionOf(file.name)] || 'file'
}
